import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { environment } from '@environments/environment';
import { CurrentUser } from '@models/Core/CurrentUser';
import { SessionType } from '@models/Sessions/Config';
import { Session } from '@models/Sessions/Session';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AuthenticationRequestService } from '@services/auth/authentication.request.service';
import { AuthenticationService } from '@services/auth/authentication.service';
import { BusService } from '@services/bus.service';
import { ConfigService, IRoleSelectOption } from '@services/config.service';
import { EventsService } from '@services/events.service';
import { SessionsRequestService } from '@services/sessions/sessions.request.service';
import { SessionsSocketService } from '@services/sockets/sessions.socket.service';
import { matchRole, sortByDate } from '@utils/sessionFilters';
import { matchCreatedBy, matchFav } from '@utils/universalFilters';

import { NewTopicModalComponent } from './new-topic-modal/new-topic-modal.component';
import { PdfSelectionModalComponent } from './pdf-selection-modal/pdf-selection-modal.component';

@Component({
  selector: 'app-sessions-topics',
  templateUrl: './topics.component.html',
  styleUrls: ['./topics.component.scss'],
})
export class SessionsTopicsComponent implements OnInit, OnDestroy {
  selectedFilter = 'All Topics';
  sessionFilter = 'all';
  sessionSort = 'Newest First';
  sessionType: SessionType = 'Topic';
  sortReverse = true;
  filterOwn = false;
  filterFav = false;
  newIdea = false;
  newTopic = false;
  permissions: any;

  sessions: Session[] = [];
  sessionsLoaded = false;
  topics: Session[] = [];
  topicClusters: Session[] = [];

  currentUser: CurrentUser;

  isAdmin = false;
  userRoles: IRoleSelectOption[] = [];

  featureFlags = environment.featureFlags;

  constructor(
    private route: ActivatedRoute,
    private bus: BusService,
    private events: EventsService,
    private authRequest: AuthenticationRequestService,
    private auth: AuthenticationService,
    private appConfig: ConfigService,
    private sessionsRequest: SessionsRequestService,
    private modalService: NgbModal,
    private sessionsSocket: SessionsSocketService,
  ) {
    this.userRoles = appConfig.rolesSelectOptions;
    this.permissions = appConfig.permissions;
  }

  get currentUserMail() {
    if (this.currentUser) {
      return this.currentUser.email;
    }

    return undefined;
  }

  get currentUserId() {
    if (this.currentUser) {
      return this.currentUser._id;
    }

    return undefined;
  }

  get sessionsByType() {
    if (this.topicClusters.length > 0 && this.sessionType === 'Topic Cluster') {
      return this.topicClusters;
    } else {
      return this.topics;
    }
  }

  get filteredParticipating() {
    return this.sessionsByType
      .filter(s =>
        matchRole(s, this.appConfig.roles, 'SESSION_PARTICIPANT', '<='),
      )
      .filter(s => matchCreatedBy(s, this.currentUserId, this.filterOwn))
      .filter(s => matchFav(s, this.filterFav))
      .sort((s1, s2) => sortByDate(s1, s2, this.sortReverse));
  }

  get filteredEvaluating() {
    return this.sessionsByType
      .filter(s =>
        matchRole(s, this.appConfig.roles, 'SESSION_EXTERNAL_EVALUATOR'),
      )
      .filter(s => matchCreatedBy(s, this.currentUserId, this.filterOwn))
      .filter(s => matchFav(s, this.filterFav))
      .sort((s1, s2) => sortByDate(s1, s2, this.sortReverse));
  }

  get selectedFilterRepresentation() {
    const postfix = this.sessionType === 'Topic' ? 'Topics' : 'Topic Clusters';
    if (this.selectedFilter === 'All Topics') {
      return `All ${postfix}`;
    } else if (this.selectedFilter === 'Own Topics') {
      return `Own ${postfix}`;
    } else if (this.selectedFilter === 'Favorite Topics') {
      return `Favorite ${postfix}`;
    } else {
      return this.selectedFilter;
    }
  }

  openNewSessionModal() {
    const modal = this.modalService.open(NewTopicModalComponent, {
      size: 'lg',
      windowClass: 'big-modal',
      beforeDismiss: () => {
        return new Promise((resolve, reject) => {
          if (!confirm(`Are you sure you want to discard this topic?`)) {
            reject();
          } else {
            resolve(true);
          }
        });
      },
    });

    modal.componentInstance.openNewIdeaModal = this.newIdea;
  }

  openPdfSelectionModal() {
    this.modalService.open(PdfSelectionModalComponent, {
      size: 'lg',
      windowClass: 'big-modal',
    });
  }

  isModerator(session: Session) {
    return session.me.role <= this.appConfig.roles.SESSION_MODERATOR;
  }

  private receiveCurrentUser(user: CurrentUser) {
    this.currentUser = user;
    this.isAdmin = this.auth.isAdmin;
  }

  private failureCurrentUser(error) {
    console.error('oopsie whoopsie', error);
  }

  // Session Data (all sessions)

  private receiveSessions(sessions: Session[]) {
    this.sessions = sessions.filter(
      s => this.isAdmin || this.isModerator(s) || !s.config.restricted,
    );
    this.sessionsLoaded = true;
    this.topics = this.sessions.filter(s => !s.config.cluster);
    this.topicClusters = this.sessions.filter(s => s.config.cluster);

    if (this.topicClusters.length === 0) {
      this.sessionType = 'Topic';
    }

    this.sessionsSocket.unsyncUpdates();
    this.sessionsSocket.syncUpdates(this.sessions);

    if (!this.topics.length && this.newIdea && !this.newTopic) {
      this.openNewSessionModal();
    }
  }

  private failureSessions(error) {
    this.sessions = [];
    console.error(error);
  }

  private subscribe() {
    this.bus.subscribe(
      this.events.received.data.user.me.success,
      this.receiveCurrentUser.bind(this),
    );
    this.bus.subscribe(
      this.events.received.data.user.me.failure,
      this.failureCurrentUser.bind(this),
    );

    this.bus.subscribe(
      this.events.received.data.session.all.success,
      this.receiveSessions.bind(this),
    );
    this.bus.subscribe(
      this.events.received.data.session.all.failure,
      this.failureSessions.bind(this),
    );
  }

  private unsubscribe() {
    this.bus.unsubscribe(
      this.events.received.data.user.me.success,
      this.receiveCurrentUser.bind(this),
    );
    this.bus.unsubscribe(
      this.events.received.data.user.me.failure,
      this.failureCurrentUser.bind(this),
    );

    this.bus.unsubscribe(
      this.events.received.data.session.all.success,
      this.receiveSessions.bind(this),
    );
    this.bus.unsubscribe(
      this.events.received.data.session.all.failure,
      this.failureSessions.bind(this),
    );
  }

  ngOnInit() {
    this.subscribe();

    this.bus.publish(this.events.requested.data.session.all);

    this.route.queryParams.subscribe(params => {
      if (
        params.newIdea &&
        (params.newIdea === true || params.newIdea === 'true')
      ) {
        this.newIdea = true;
      } else {
        this.newIdea = false;
      }

      if (
        params.newTopic &&
        (params.newTopic === true || params.newTopic === 'true')
      ) {
        this.openNewSessionModal();
      }
    });
  }

  ngOnDestroy() {
    this.unsubscribe();

    this.sessionsSocket.unsyncUpdates();
  }
}
